import { useSuspenseQuery } from '@apollo/client';
import { useMemo } from 'react';

import { ClickReasonsEnum } from '@xing-com/crate-common-graphql-types';
import { useLocation, useNavigate } from '@xing-com/crate-core-router';
import {
  createEntityPageShared,
  EntityPageDocument,
  PageContextType,
} from '@xing-com/crate-entity-pages-common';
import { useFeatureSwitches } from '@xing-com/hub';

import { modulesMap } from '../../config/modules-map';
import { EP_URL } from '../../config/urls';
import { getPathByFocusType } from '../../utils/get-path-by-focus-type';

const isModuleActive = (featureSwitches: string[], moduleType: string) => {
  // if the module is not tied to any feature switch return true
  if (!modulesMap[moduleType].featureSwitch) {
    return true;
  }
  const featureSwitch = modulesMap[moduleType].featureSwitch;
  return featureSwitch && featureSwitches.includes(featureSwitch);
};

type EntityPageHookResult =
  | {
      data: PageContextType;
      redirectTo: undefined;
      errorCode: undefined;
    }
  | {
      data: undefined;
      redirectTo: string;
      errorCode: undefined;
    }
  | {
      data: undefined;
      redirectTo: undefined;
      errorCode: 404 | 500 | 503;
    };

export function useEntityPage({
  pageSlug,
}: {
  pageSlug: string;
}): EntityPageHookResult {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: featureSwitches = [] } = useFeatureSwitches();
  const path = location.pathname?.split('/')[1];
  const { data, error } = useSuspenseQuery(EntityPageDocument, {
    variables: {
      id: pageSlug,
      socialProofClickReasonsKey:
        ClickReasonsEnum.CrWebPublisherSocialProofHeader,
    },
    errorPolicy: 'all',
  });

  return useMemo((): EntityPageHookResult => {
    const entityPage = data?.entityPageEX;

    if (error || entityPage?.__typename !== 'EntityPage') {
      switch (entityPage?.__typename) {
        case 'EntityPageError': {
          return {
            data: undefined,
            redirectTo: undefined,
            errorCode: entityPage.errorCode === 10404 ? 404 : 503,
          };
        }
        case 'EntityPageMoved': {
          return {
            data: undefined,
            redirectTo: EP_URL(entityPage.slug),
            errorCode: undefined,
          };
        }
        default: {
          return { data: undefined, redirectTo: undefined, errorCode: 503 };
        }
      }
    }

    if (entityPage.publicationStatus === 'CREATED') {
      return {
        data: undefined,
        errorCode: undefined,
        redirectTo: '/pages/new/company',
      };
    }

    if (getPathByFocusType() !== `/${path}`) {
      return { data: undefined, redirectTo: undefined, errorCode: 404 };
    }

    const pageModules =
      entityPage.modules?.collection.filter(
        (module) =>
          module?.type !== 'header' &&
          module?.type &&
          modulesMap[module?.type] &&
          isModuleActive(featureSwitches, module?.type)
      ) ?? [];

    const pageModulesByType = pageModules
      .map((module) => module?.type)
      .filter((type) => type !== undefined);

    if (!pageModulesByType?.length) {
      return { data: undefined, redirectTo: undefined, errorCode: 500 };
    }

    const hasNewsModule = !!pageModules?.find(
      (pageModule) => pageModule?.type === 'news'
    );

    const headerModule = entityPage.modules?.collection.find(
      (module) => module?.type === 'header'
    );
    const validHeaderModule =
      headerModule?.__typename === 'EntityPageHeaderModule'
        ? headerModule
        : undefined;
    const headerModuleCompanyIndustry =
      validHeaderModule?.content?.__typename ===
      'EntityPageCompanyHeaderContent'
        ? validHeaderModule?.content.company?.industry
        : undefined;

    const sharedEntityPageProperties = createEntityPageShared(entityPage);

    return {
      data: {
        ...sharedEntityPageProperties,
        // Extra
        industryId: headerModuleCompanyIndustry?.id,
        hasNewsModule,
        pageModulesByType,
        headerModule: validHeaderModule,
        // @ts-expect-error TODO: fix this type once Article Module is released
        pageModules,
        goBackUrl: entityPage.globalId
          ? () => navigate(-1)
          : () => navigate(EP_URL(entityPage.slug)),
      },
      redirectTo: undefined,
      error: undefined,
    };
  }, [path, pageSlug, data, error]);
}
